import { CurrencyPipe } from '@angular/common';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, Renderer2, TemplateRef, ViewChild } from '@angular/core';
import { PackageReport } from '@models/reports/package/package-report';
import { Tax } from '@models/reports/tax/tax';
import { DataStateChangeEvent, GridComponent, GridDataResult } from '@progress/kendo-angular-grid';
import { DataSourceRequestState, SortDescriptor, orderBy } from '@progress/kendo-data-query';
import { ReportsService } from '@services/reports.service';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { minusIcon, plusIcon } from '@progress/kendo-svg-icons';

@Component({
  selector: 'app-package-reports',
  templateUrl: './package-reports.component.html',
  styleUrls: ['./package-reports.component.less'],
})
export class PackageReportsComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('grid') private grid: GridComponent;
  @ViewChild('expandAll') private expandAll: ElementRef;
  gridView: GridDataResult;
  gridState: DataSourceRequestState;
  loading: boolean = false;
  possibleTaxes: Tax[] = [];
  unsub: Subject<void> = new Subject<void>();
  expandedDetailKeys: any[] = [];
  plusIcon = plusIcon;
  minusIcon = minusIcon;

  get allCollapsed() {
    return this.expandedDetailKeys.length === 0;
  }

  constructor(
    private reportsService: ReportsService,
    private currencyPipe: CurrencyPipe,
    private elementRef: ElementRef,
    private renderer: Renderer2
  ) {}

  ngOnInit() {
    this.initDefaultValue();
    this.getPossibleTaxes();
    this.getReportData();
    this.allData = this.allData.bind(this);
  }

  ngAfterViewInit() {
    const hierarchyCell = this.elementRef.nativeElement.querySelector('.k-hierarchy-cell') as HTMLElement;
    this.renderer.appendChild(hierarchyCell, this.expandAll.nativeElement);
  }

  initDefaultValue() {
    this.gridState = {
      skip: 0,
      take: 100,
      // set default state of sorting icon
      sort: [{ field: 'packageName', dir: 'desc' }],
    };

    this.gridView = {
      data: [],
      total: 0,
    };
  }

  toggleExpandAll() {
    this.expandedDetailKeys = this.allCollapsed
      ? this.gridView.data.map((item: PackageReport) => this.expandDetailsBy(item))
      : [];
  }

  expandDetailsBy = (dataItem: PackageReport) => {
    return dataItem.clinicServiceTemplateId + '-' + dataItem.packageId;
  };

  getPossibleTaxes() {
    this.reportsService
      .getServicePossibleTaxes()
      .pipe(takeUntil(this.unsub))
      .subscribe((taxes: Tax[]) => {
        taxes.forEach((tax: Tax) => {
          if (!this.possibleTaxes.some((possibleTax: Tax) => possibleTax.id === tax.id)) {
            this.possibleTaxes.push(tax);
          }
        });
      });
  }

  getReportData() {
    this.loading = true;

    this.reportsService.getPackageReportEntities(this.gridState).subscribe((packageReports: any) => {
      this.gridView = {
        data: packageReports.data,
        total: packageReports.total || packageReports.data.length,
      };
      this.loading = false;
      this.expandedDetailKeys = [];
    });
  }

  exportToExcel() {
    this.grid.saveAsExcel();
  }

  onExcelExport(e: any) {
    this.reportsService.onExcelExport(e, 'Packages Report');
  }

  pageChange(gridState: DataStateChangeEvent) {
    this.gridState = gridState;
    this.getReportData();
  }

  sortChange(sort: SortDescriptor[]) {
    this.gridState.sort = sort;
    this.gridView.data = orderBy(this.gridView.data, this.gridState.sort);
  }

  sortIconClass(column: any) {
    return this.gridState.sort.length &&
      this.gridState.sort[0].field === column.field &&
      this.gridState.sort[0].dir === 'asc'
      ? 'fad fa-sort-up'
      : this.gridState.sort.length &&
        this.gridState.sort[0].field === column.field &&
        this.gridState.sort[0].dir === 'desc'
      ? 'fad fa-sort-down'
      : 'fas fa-sort';
  }

  allData = (): Observable<any> => {
    const gridState: DataSourceRequestState = {};
    return this.reportsService.getPackageReportEntities(gridState).pipe(
      map((packageReports: any) => {
        return {
          data: packageReports.data.map((element: PackageReport) => {
            this.possibleTaxes.forEach((item) => {
              element[item.name] = this.currencyPipe.transform(element.taxCollected[item.name]);
            });
            return {
              ...element,
              salesAmount: this.currencyPipe.transform(element.salesAmount),
            };
          }),
          total: packageReports.total,
        };
      })
    );
  };

  ngOnDestroy() {
    this.unsub.next();
    this.unsub.complete();
  }
}
